import { useDisclosure } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AccountDocumentContactForm,
  AccountDocumentContactFormSchema,
  AccountDocumentDetailResponse,
  AccountDocumentStatusEnum,
  IAccountRespSchema,
} from '@monetize/types/app';
import { DEFAULT_PAGER } from '@monetize/types/common';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdClose } from 'react-icons/md';
import {
  useGetAccountDocumentSignerStats,
  useUpdateAccountDocument,
  useUpdateAccountDocumentContact,
} from '../../../../api/accountsService';
import {
  useGetAccountContacts,
  useGetEsignContacts,
} from '../../../../api/contactsService';
import { ContactFormModal } from '../../../../components/Contacts/ContactFormModal';
import { ContactSelectWithData } from '../../../../components/Contacts/ContactSelectWithData';
import {
  MCustomIconButton,
  MFormField,
  MText,
} from '../../../../components/Monetize';
import {
  MBox,
  MFlex,
  MGrid,
  MGridItem,
} from '../../../../components/Monetize/chakra';
import { ESignRecipientItem } from '../../../../components/Quotes/ESignRecipientItem';
import { EMPTY_ARRAY } from '../../../../constants/common';
import { useACL } from '../../../../services/acl/acl';
import { SectionHeader } from './SectionHeader';

interface AccountDocumentContactSectionProps {
  accountDocument: AccountDocumentDetailResponse;
  isReadOnly?: boolean;
  refetchAccountDocument: () => void;
  account: IAccountRespSchema;
}

export const AccountDocumentContactSection = ({
  accountDocument,
  isReadOnly,
  refetchAccountDocument,
  account,
}: AccountDocumentContactSectionProps) => {
  const { accountId } = accountDocument;
  const { data: signerStats, isLoading: isLoadingSignerStats } =
    useGetAccountDocumentSignerStats(accountId, accountDocument.id, {
      enabled:
        accountDocument.status === AccountDocumentStatusEnum.SENT ||
        accountDocument.status === AccountDocumentStatusEnum.SIGNED,
    });

  const contactsModalState = useDisclosure();
  const [editingFieldName, setEditingFieldName] = useState<
    'primaryExternalId' | 'primaryInternalId' | 'esignCcIds'
  >('primaryExternalId');
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors, isValid, isSubmitting },
    watch,
    reset,
  } = useForm<AccountDocumentContactForm>({
    resolver: zodResolver(AccountDocumentContactFormSchema),
    defaultValues: { esignCcIds: [] },
    mode: 'onChange',
  });
  const {
    mutateAsync: doUpdateAccountDocument,
    isPending: isUpdatingAccountDocument,
  } = useUpdateAccountDocument({
    onSuccess: async () => {
      await refetchAccountDocument();
      setIsEditMode(false);
    },
  });

  useEffect(() => {
    if (accountDocument) {
      let primaryExternalId = '';
      let primaryInternalId = '';
      accountDocument.contacts.primaryESignContacts.forEach((contact) => {
        if (contact.internal) {
          primaryInternalId = contact.id;
        } else {
          primaryExternalId = contact.id;
        }
      });

      reset({
        primaryExternalId,
        primaryInternalId,
        esignCcIds:
          accountDocument.contacts.additionalESignRecipients.map(
            (contact) => contact.id,
          ) || [],
      });
    }
  }, [accountDocument, reset]);

  const { data: accountContacts, isLoading: loadingExternalContacts } =
    useGetAccountContacts(
      {
        accountId: account.id,
        config: {
          ...DEFAULT_PAGER,
          sortField: 'fullName',
          sortOrder: 1,
          rows: 100,
        },
        isGetAll: true,
      },
      {
        enabled: !!accountId,
        select: (response) => response?.content || EMPTY_ARRAY,
        refetchOnWindowFocus: false,
      },
    );
  const { allContacts: esignContacts, isLoading: loadingInternalContacts } =
    useGetEsignContacts({ accountId, refetchOnWindowFocus: false });

  const [internalContacts, externalContacts] = useMemo(() => {
    return [
      [
        ...(accountContacts?.filter((contact) => contact.internal) || []),
        ...esignContacts,
      ],
      accountContacts?.filter((contact) => !contact.internal) || [],
    ];
  }, [esignContacts, accountContacts]);

  const {
    isPending: isUpdatingAccountDocumentContact,
    mutate: doUpdateContact,
  } = useUpdateAccountDocumentContact({
    onSuccess: async () => {
      await refetchAccountDocument();
      setIsEditMode(false);
    },
  });

  const onSubmit = async (payload: AccountDocumentContactForm) => {
    doUpdateContact({
      accountId,
      accountDocumentId: accountDocument?.id,
      payload: {
        esignPrimaryIds: [payload.primaryInternalId, payload.primaryExternalId],
        esignSecondaryIds: [],
        esignCcIds: payload.esignCcIds,
      },
    });
  };

  const { canDo } = useACL();

  const canCreateContact =
    canDo([['account_contacts', 'create']]) && !!accountId;

  const isReadOnlyProps = {
    isDisabled: !isEditMode,
    disabled: !isEditMode,
    isReadOnly: !isEditMode,
    variant: !isEditMode ? 'readonly' : 'outline',
  } as const;

  const showSignerStats =
    accountDocument.status === AccountDocumentStatusEnum.SIGNED ||
    accountDocument.status === AccountDocumentStatusEnum.SENT;
  const esignCcIdsWatch = watch('esignCcIds');
  const primaryExternalIdWatch = watch('primaryExternalId');
  const {
    selectedExternalContacts,
    unselectedExternalContacts,
    primaryExternalRecipient,
    primaryInternalRecipient,
    ccRecipients,
  } = useMemo(() => {
    return {
      selectedExternalContacts:
        externalContacts?.filter((contact) =>
          esignCcIdsWatch.includes(contact.id),
        ) || [],
      unselectedExternalContacts:
        externalContacts?.filter(
          (contact) =>
            !esignCcIdsWatch.includes(contact.id) &&
            contact.id !== primaryExternalIdWatch,
        ) || [],
      primaryExternalRecipient: signerStats?.signers.find(
        (signer) =>
          signer.email ===
          accountDocument.contacts.primaryESignContacts.find(
            ({ internal }) => !internal,
          )?.email,
      ),
      primaryInternalRecipient: signerStats?.signers.find(
        (signer) =>
          signer.email ===
          accountDocument.contacts.primaryESignContacts.find(
            ({ internal }) => internal,
          )?.email,
      ),
      ccRecipients: signerStats?.ccContacts,
    };
  }, [
    externalContacts,
    esignCcIdsWatch,
    primaryExternalIdWatch,
    accountDocument,
    signerStats,
  ]);

  return (
    <MBox width="100%" flex="1" ml="5">
      <form
        id="account-document-form"
        data-testid="account-document-form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <SectionHeader
          label="Contacts"
          isEditMode={isEditMode}
          isLoading={isUpdatingAccountDocument}
          onEdit={() => {
            setIsEditMode((prevState) => !prevState);
          }}
          onSave={() => {
            handleSubmit(onSubmit)();
          }}
          saveButtonProps={{
            isDisabled: !isValid || isSubmitting,
            isLoading: isUpdatingAccountDocumentContact,
          }}
          canEdit={!isReadOnly}
        />
        <MGrid templateColumns="repeat(6, 1fr)" gap={4}>
          <MGridItem colSpan={3}>
            <MFormField
              label="Primary External Contact"
              error={errors?.primaryExternalId}
              isRequired
            >
              {!showSignerStats && (
                <Controller
                  name="primaryExternalId"
                  control={control}
                  render={({ field: { value, onChange, ...field } }) => (
                    <ContactSelectWithData
                      loading={loadingExternalContacts}
                      contacts={externalContacts || []}
                      onChange={(val) => {
                        onChange(val);
                      }}
                      onNewContact={() => {
                        setEditingFieldName('primaryExternalId');
                        contactsModalState.onOpen();
                      }}
                      canCreateContact={canCreateContact}
                      value={value || ''}
                      {...field}
                      {...isReadOnlyProps}
                    />
                  )}
                />
              )}
              {showSignerStats && (
                <ESignRecipientItem
                  recipient={primaryExternalRecipient}
                  ml="0"
                  isLoading={isLoadingSignerStats}
                />
              )}
            </MFormField>
          </MGridItem>
          <MGridItem colSpan={3}>
            <MFormField
              label="Primary Internal Contact"
              error={errors?.primaryInternalId}
              isRequired
            >
              {!showSignerStats && (
                <Controller
                  name="primaryInternalId"
                  control={control}
                  render={({ field: { value, onChange, ...field } }) => (
                    <ContactSelectWithData
                      loading={loadingInternalContacts}
                      contacts={internalContacts || []}
                      onChange={(val) => {
                        onChange(val);
                      }}
                      onNewContact={() => {
                        setEditingFieldName('primaryInternalId');
                        contactsModalState.onOpen();
                      }}
                      canCreateContact={canCreateContact}
                      value={value || ''}
                      {...field}
                      {...isReadOnlyProps}
                    />
                  )}
                />
              )}
              {showSignerStats && (
                <ESignRecipientItem
                  recipient={primaryInternalRecipient}
                  ml="0"
                  isLoading={isLoadingSignerStats}
                />
              )}
            </MFormField>
          </MGridItem>
          <MGridItem colSpan={6} />
          <MGridItem colSpan={6}>
            <MFormField label="Additional Recipients">
              {!showSignerStats &&
                selectedExternalContacts.map((contact) => (
                  <MFlex key={contact.id} justifyContent="space-between" my="2">
                    <MText>
                      {contact.fullName}{' '}
                      {contact.email ? `(${contact.email})` : ''}
                    </MText>
                    {isEditMode && (
                      <MCustomIconButton
                        btnSize={5}
                        variant="icon"
                        icon={MdClose}
                        aria-label="Remove recipient"
                        onClick={() => {
                          setValue(
                            'esignCcIds',
                            esignCcIdsWatch.filter((id) => id !== contact.id),
                          );
                        }}
                      />
                    )}
                  </MFlex>
                ))}

              {showSignerStats &&
                ccRecipients?.map((recipient, index) => (
                  <ESignRecipientItem
                    ml="0"
                    recipient={recipient}
                    key={index}
                    isCC
                    isLoading={isLoadingSignerStats}
                  />
                ))}
              {isEditMode && (
                <ContactSelectWithData
                  loading={loadingExternalContacts}
                  contacts={unselectedExternalContacts || []}
                  onChange={(val) => {
                    setValue('esignCcIds', [...esignCcIdsWatch, val]);
                  }}
                  onNewContact={() => {
                    setEditingFieldName('esignCcIds');
                    contactsModalState.onOpen();
                  }}
                  canCreateContact={canCreateContact}
                  {...isReadOnlyProps}
                />
              )}
            </MFormField>
          </MGridItem>
        </MGrid>
      </form>

      {contactsModalState.open && (
        <ContactFormModal
          isOpen={contactsModalState.open}
          defaultValues={{
            internal: editingFieldName === 'primaryInternalId',
            esigner: editingFieldName === 'primaryInternalId',
          }}
          accountId={accountId}
          onClose={contactsModalState.onClose}
          onCreatedContact={(contact) => {
            contactsModalState.onClose();
            if (editingFieldName === 'esignCcIds') {
              setValue(editingFieldName, [...esignCcIdsWatch, contact.id]);
            } else {
              setValue(editingFieldName, contact.id);
            }
          }}
        />
      )}
    </MBox>
  );
};
